import { useEffect, useState } from 'react';

// 1px tolerance: browsers round sub-pixel line heights, so content can measure
// 1px taller than its box even when nothing is actually cut off.
export const isClipped = (contentHeight: number, boxHeight: number) =>
  contentHeight - boxHeight > 1;

// Height that ends at the bottom of the lowest text line fitting within
// `limit`. Clipping at a fixed max-height usually cuts a line in half or ends
// on a paragraph margin, leaving a blank gap above the "Read more" button.
export const snapToLastLine = (lineBottoms: number[], limit: number) => {
  const fitting = lineBottoms.filter((bottom) => bottom <= limit + 0.5);
  return fitting.length ? Math.ceil(Math.max(...fitting)) : limit;
};

// Bottoms of the rendered text lines inside `content`, relative to `top`.
// Stops once lines start below `limit` – nothing after that is visible.
const getLineBottoms = (content: HTMLElement, top: number, limit: number) => {
  const bottoms: number[] = [];
  const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.textContent?.trim()) continue;
    range.selectNodeContents(node);
    const rects = Array.from(range.getClientRects());
    if (rects.length && rects[0].top - top > limit) break;
    rects.forEach((rect) => bottoms.push(rect.bottom - top));
  }
  return bottoms;
};

interface Clip {
  isOverflowing: boolean;
  clipHeight?: number;
}

// The limit comes from the box's CSS max-height; the inline height set from
// clipHeight doesn't change it, so re-measuring is stable.
const measureClip = (box: HTMLElement, content: HTMLElement): Clip => {
  const limit = parseFloat(getComputedStyle(box).maxHeight);
  if (Number.isNaN(limit)) return { isOverflowing: false };

  const top = box.getBoundingClientRect().top;
  const contentHeight = content.getBoundingClientRect().bottom - top;
  if (!isClipped(contentHeight, limit)) return { isOverflowing: false };

  return {
    isOverflowing: true,
    clipHeight: snapToLastLine(getLineBottoms(content, top, limit), limit),
  };
};

// Calls onResize now and whenever any of the elements resizes. Both the box
// and its content must be observed: once the box is capped at its max-height
// it stops resizing, so content growth (new text after client-side
// navigation, client-only markdown mounting, fonts loading) is only visible
// on the unconstrained inner content element.
export const watchResize = (elements: HTMLElement[], onResize: () => void) => {
  onResize();
  const observer = new ResizeObserver(onResize);
  elements.forEach((element) => observer.observe(element));
  // A breakpoint can change the box's max-height while neither element
  // resizes (the box is pinned by its inline clip height and the content
  // width can be the same on both sides of the breakpoint)
  window.addEventListener('resize', onResize);
  return () => {
    observer.disconnect();
    window.removeEventListener('resize', onResize);
  };
};

// Tells whether the content is taller than its box (text cut off by a
// max-height + overflow-hidden) and, if so, the height to clip at so the box
// ends on a whole line. Attach boxRef to the clipping element (it must have a
// max-height) and contentRef to a wrapper around the content inside it.
// Callback refs (not useRef) so the check re-runs when the elements mount
// later, e.g. a description that arrives after client-side navigation.
// Pass enabled=false while the element is expanded: an expanded box never
// overflows, so measuring then would hide the "Hide" button.
export const useIsOverflowing = (enabled = true) => {
  const [box, setBox] = useState<HTMLElement | null>(null);
  const [content, setContent] = useState<HTMLElement | null>(null);
  const [clip, setClip] = useState<Clip>({ isOverflowing: false });

  useEffect(() => {
    if (!box || !content || !enabled) return;
    return watchResize([box, content], () => {
      const next = measureClip(box, content);
      // Keep the same object when nothing changed so React skips the render
      setClip((prev) =>
        prev.isOverflowing === next.isOverflowing &&
        prev.clipHeight === next.clipHeight
          ? prev
          : next
      );
    });
  }, [box, content, enabled]);

  return {
    boxRef: setBox,
    contentRef: setContent,
    isOverflowing: clip.isOverflowing,
    clipHeight: clip.clipHeight,
  };
};
